import React, { createContext, useContext, useState, useCallback, useEffect, useRef, } from 'react';
// Create context
const S3BrowserContext = createContext(null);
// Custom hook to use S3Browser context
export const useS3Browser = () => {
    const context = useContext(S3BrowserContext);
    if (!context) {
        throw new Error('useS3Browser must be used within an S3BrowserProvider');
    }
    return context;
};
// Helper function to normalize S3 paths to ensure proper trailing slash for folders
const normalizeS3Path = (path) => {
    if (!path)
        return ''; // Empty path is root
    // Remove trailing slash, then add it back to ensure consistency
    const cleanPath = path.replace(/\/+$/, '');
    return cleanPath ? cleanPath + '/' : '';
};
// Helper function to parse path into breadcrumbs
const parseBreadcrumbs = (path) => {
    if (!path) {
        return [{ path: '', name: 'Reports' }];
    }
    const segments = path.split('/').filter(Boolean);
    const breadcrumbs = [{ path: '', name: 'Reports' }];
    let currentPath = '';
    for (const segment of segments) {
        currentPath += (currentPath ? '/' : '') + segment;
        // Always add trailing slash for folder paths to ensure S3 prefix validation passes
        const breadcrumbPath = currentPath + '/';
        breadcrumbs.push({
            path: breadcrumbPath,
            name: decodeURIComponent(segment)
        });
    }
    return breadcrumbs;
};
// Helper function to reconcile selections with filtered reports
// Policy: Retain all selections, but warn about selections not visible in filtered results
const reconcileSelections = (selectedItems, filteredReports) => {
    if (!filteredReports) {
        // No filtering active - all selections are visible
        return {
            visibleSelections: selectedItems,
            hiddenSelections: [],
        };
    }
    const filteredKeys = new Set(filteredReports.map(item => item.key));
    const visibleSelections = [];
    const hiddenSelections = [];
    for (const item of selectedItems) {
        if (filteredKeys.has(item.key)) {
            visibleSelections.push(item);
        }
        else {
            hiddenSelections.push(item);
        }
    }
    return { visibleSelections, hiddenSelections };
};
// Initial state
const createInitialState = (initialPath = '') => ({
    navigation: {
        currentPath: initialPath,
        pathHistory: [initialPath],
        historyIndex: 0,
        breadcrumbs: parseBreadcrumbs(initialPath),
    },
    selection: {
        selectedItems: [],
        selectedItemKeys: new Set(),
        selectionCount: 0,
        selectedFiles: [],
        selectedFolders: [],
    },
    loading: false,
    error: null,
});
// S3Browser provider component
export const S3BrowserProvider = ({ children, initialPath = '', filteredReports, isLoading, error, paginationMetadata, onPageChange, onPageSizeChange }) => {
    const [state, setState] = useState(() => createInitialState(initialPath));
    // Pagination state management for controlled mode
    const [internalPage, setInternalPage] = useState(0);
    const [internalPageSize] = useState(50);
    // Track previous filtered reports length to detect when filters change
    const previousFilteredCountRef = useRef(undefined);
    // Track previous selected items for change detection
    const previousSelectedItemsRef = useRef(state.selection.selectedItems);
    // Update the ref when selected items change
    useEffect(() => {
        previousSelectedItemsRef.current = state.selection.selectedItems;
    }, [state.selection.selectedItems]);
    // Helper function to calculate total pages
    const calculateTotalPages = (itemCount, pageSize) => {
        if (itemCount <= 0)
            return 1;
        return Math.ceil(itemCount / pageSize);
    };
    // Helper function to clamp page number within valid range (0-indexed)
    const clampPage = (page, totalPages) => {
        if (totalPages <= 1)
            return 0; // No pagination needed
        const maxPage = totalPages - 1;
        return Math.max(0, Math.min(page, maxPage));
    };
    // Calculate total pages based on filtered reports
    const totalPagesCount = filteredReports !== undefined
        ? calculateTotalPages(filteredReports.length, internalPageSize)
        : undefined;
    // Effect: Reset pagination to page 0 when filters change or filteredReports updates
    useEffect(() => {
        if (filteredReports !== undefined) {
            const currentFilteredCount = filteredReports.length;
            // If filtered results count changed, reset to first page
            if (previousFilteredCountRef.current !== undefined &&
                previousFilteredCountRef.current !== currentFilteredCount) {
                setInternalPage(0);
                if (onPageChange) {
                    onPageChange(0);
                }
            }
            previousFilteredCountRef.current = currentFilteredCount;
        }
    }, [filteredReports?.length, onPageChange]);
    // Effect: Clamp current page within valid range when total pages changes
    useEffect(() => {
        if (totalPagesCount !== undefined && internalPage >= totalPagesCount) {
            // If current page is beyond the new total pages, clamp it
            const clampedPage = clampPage(internalPage, totalPagesCount);
            if (clampedPage !== internalPage) {
                setInternalPage(clampedPage);
                if (onPageChange) {
                    onPageChange(clampedPage);
                }
            }
        }
    }, [totalPagesCount, internalPage, onPageChange]);
    // Effect: Reconcile selection state when filteredReports changes
    // Policy: Retain selections for items filtered out, but log warnings
    useEffect(() => {
        if (filteredReports !== undefined && state.selection.selectedItems.length > 0) {
            const { visibleSelections, hiddenSelections } = reconcileSelections(state.selection.selectedItems, filteredReports);
            // If there are hidden selections (items filtered out but still selected),
            // log a warning for debugging purposes. This is intentional behavior
            // to preserve selections across filter changes for bulk downloads.
            if (hiddenSelections.length > 0) {
                console.warn(`[S3Browser] Selection reconciliation: ${hiddenSelections.length} selected item(s) are not visible in current filtered results. ` +
                    `Retaining ${visibleSelections.length} visible selection(s) for bulk download. ` +
                    `Hidden items: ${hiddenSelections.map(item => item.name).join(', ')}`);
            }
        }
    }, [filteredReports, state.selection.selectedItems]);
    // Navigation: Navigate to a specific path
    const navigateTo = useCallback((path) => {
        setState(prevState => {
            // Normalize the path to ensure proper trailing slash for S3 prefix validation
            const normalizedPath = normalizeS3Path(path);
            const newHistory = [...prevState.navigation.pathHistory.slice(0, prevState.navigation.historyIndex + 1), normalizedPath];
            return {
                ...prevState,
                navigation: {
                    ...prevState.navigation,
                    currentPath: normalizedPath,
                    pathHistory: newHistory,
                    historyIndex: newHistory.length - 1,
                    breadcrumbs: parseBreadcrumbs(normalizedPath),
                },
                // Clear selection when navigating to new path
                selection: {
                    selectedItems: [],
                    selectedItemKeys: new Set(),
                    selectionCount: 0,
                    selectedFiles: [],
                    selectedFolders: [],
                },
            };
        });
    }, []);
    // Navigation: Go back in history
    const navigateBack = useCallback(() => {
        setState(prevState => {
            if (prevState.navigation.historyIndex <= 0)
                return prevState;
            const newIndex = prevState.navigation.historyIndex - 1;
            const newPath = prevState.navigation.pathHistory[newIndex];
            return {
                ...prevState,
                navigation: {
                    ...prevState.navigation,
                    currentPath: newPath,
                    historyIndex: newIndex,
                    breadcrumbs: parseBreadcrumbs(newPath),
                },
                selection: {
                    selectedItems: [],
                    selectedItemKeys: new Set(),
                    selectionCount: 0,
                    selectedFiles: [],
                    selectedFolders: [],
                },
            };
        });
    }, []);
    // Navigation: Go forward in history
    const navigateForward = useCallback(() => {
        setState(prevState => {
            if (prevState.navigation.historyIndex >= prevState.navigation.pathHistory.length - 1) {
                return prevState;
            }
            const newIndex = prevState.navigation.historyIndex + 1;
            const newPath = prevState.navigation.pathHistory[newIndex];
            return {
                ...prevState,
                navigation: {
                    ...prevState.navigation,
                    currentPath: newPath,
                    historyIndex: newIndex,
                    breadcrumbs: parseBreadcrumbs(newPath),
                },
                selection: {
                    selectedItems: [],
                    selectedItemKeys: new Set(),
                    selectionCount: 0,
                    selectedFiles: [],
                    selectedFolders: [],
                },
            };
        });
    }, []);
    // Navigation: Go to parent directory
    const navigateUp = useCallback(() => {
        setState(prevState => {
            const currentPath = prevState.navigation.currentPath;
            if (!currentPath)
                return prevState; // Already at root
            const segments = currentPath.split('/').filter(Boolean);
            const parentPath = segments.length > 1
                ? segments.slice(0, -1).join('/') + '/'
                : ''; // Go to root if only one segment
            const newHistory = [...prevState.navigation.pathHistory.slice(0, prevState.navigation.historyIndex + 1), parentPath];
            return {
                ...prevState,
                navigation: {
                    ...prevState.navigation,
                    currentPath: parentPath,
                    pathHistory: newHistory,
                    historyIndex: newHistory.length - 1,
                    breadcrumbs: parseBreadcrumbs(parentPath),
                },
                selection: {
                    selectedItems: [],
                    selectedItemKeys: new Set(),
                    selectionCount: 0,
                    selectedFiles: [],
                    selectedFolders: [],
                },
            };
        });
    }, []);
    // Navigation: Go to root directory
    const navigateToRoot = useCallback(() => {
        navigateTo('');
    }, [navigateTo]);
    // Selection: Select a single item
    const selectItem = useCallback((item) => {
        setState(prevState => {
            if (prevState.selection.selectedItemKeys.has(item.key)) {
                return prevState; // Already selected
            }
            const newSelectedItems = [...prevState.selection.selectedItems, item];
            const newSelectedItemKeys = new Set(prevState.selection.selectedItemKeys);
            newSelectedItemKeys.add(item.key);
            const selectedFiles = newSelectedItems.filter(i => i.type === 'file');
            const selectedFolders = newSelectedItems.filter(i => i.type === 'folder');
            return {
                ...prevState,
                selection: {
                    selectedItems: newSelectedItems,
                    selectedItemKeys: newSelectedItemKeys,
                    selectionCount: newSelectedItems.length,
                    selectedFiles,
                    selectedFolders,
                },
            };
        });
    }, []);
    // Selection: Deselect an item by key
    const deselectItem = useCallback((itemKey) => {
        setState(prevState => {
            if (!prevState.selection.selectedItemKeys.has(itemKey)) {
                return prevState; // Not selected
            }
            const newSelectedItems = prevState.selection.selectedItems.filter(item => item.key !== itemKey);
            const newSelectedItemKeys = new Set(prevState.selection.selectedItemKeys);
            newSelectedItemKeys.delete(itemKey);
            const selectedFiles = newSelectedItems.filter(i => i.type === 'file');
            const selectedFolders = newSelectedItems.filter(i => i.type === 'folder');
            return {
                ...prevState,
                selection: {
                    selectedItems: newSelectedItems,
                    selectedItemKeys: newSelectedItemKeys,
                    selectionCount: newSelectedItems.length,
                    selectedFiles,
                    selectedFolders,
                },
            };
        });
    }, []);
    // Selection: Toggle item selection
    const toggleItemSelection = useCallback((item) => {
        setState(prevState => {
            const isSelected = prevState.selection.selectedItemKeys.has(item.key);
            if (isSelected) {
                // Remove from selection
                const newSelectedItems = prevState.selection.selectedItems.filter(i => i.key !== item.key);
                const newSelectedItemKeys = new Set(prevState.selection.selectedItemKeys);
                newSelectedItemKeys.delete(item.key);
                const selectedFiles = newSelectedItems.filter(i => i.type === 'file');
                const selectedFolders = newSelectedItems.filter(i => i.type === 'folder');
                return {
                    ...prevState,
                    selection: {
                        selectedItems: newSelectedItems,
                        selectedItemKeys: newSelectedItemKeys,
                        selectionCount: newSelectedItems.length,
                        selectedFiles,
                        selectedFolders,
                    },
                };
            }
            else {
                // Add to selection
                const newSelectedItems = [...prevState.selection.selectedItems, item];
                const newSelectedItemKeys = new Set(prevState.selection.selectedItemKeys);
                newSelectedItemKeys.add(item.key);
                const selectedFiles = newSelectedItems.filter(i => i.type === 'file');
                const selectedFolders = newSelectedItems.filter(i => i.type === 'folder');
                return {
                    ...prevState,
                    selection: {
                        selectedItems: newSelectedItems,
                        selectedItemKeys: newSelectedItemKeys,
                        selectionCount: newSelectedItems.length,
                        selectedFiles,
                        selectedFolders,
                    },
                };
            }
        });
    }, []);
    // Selection: Select multiple items (replace current selection)
    const selectItems = useCallback((items) => {
        setState(prevState => {
            const newSelectedItemKeys = new Set(items.map(item => item.key));
            const selectedFiles = items.filter(i => i.type === 'file');
            const selectedFolders = items.filter(i => i.type === 'folder');
            return {
                ...prevState,
                selection: {
                    selectedItems: items,
                    selectedItemKeys: newSelectedItemKeys,
                    selectionCount: items.length,
                    selectedFiles,
                    selectedFolders,
                },
            };
        });
    }, []);
    // Selection: Select all items from provided list
    const selectAllItems = useCallback((items) => {
        selectItems(items);
    }, [selectItems]);
    // Selection: Clear all selections
    const clearSelection = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            selection: {
                selectedItems: [],
                selectedItemKeys: new Set(),
                selectionCount: 0,
                selectedFiles: [],
                selectedFolders: [],
            },
        }));
    }, []);
    // Selection: Check if item is selected
    const isItemSelected = useCallback((itemKey) => {
        return state.selection.selectedItemKeys.has(itemKey);
    }, [state.selection.selectedItemKeys]);
    // Get selected files only
    const getSelectedFiles = useCallback(() => {
        return state.selection.selectedFiles;
    }, [state.selection.selectedFiles]);
    // Get selected folders only
    const getSelectedFolders = useCallback(() => {
        return state.selection.selectedFolders;
    }, [state.selection.selectedFolders]);
    // Get visible selections (items currently shown in filtered view)
    const getVisibleSelections = useCallback(() => {
        const { visibleSelections } = reconcileSelections(state.selection.selectedItems, filteredReports);
        return visibleSelections;
    }, [state.selection.selectedItems, filteredReports]);
    // Get hidden selections (items selected but not visible due to filtering)
    const getHiddenSelections = useCallback(() => {
        const { hiddenSelections } = reconcileSelections(state.selection.selectedItems, filteredReports);
        return hiddenSelections;
    }, [state.selection.selectedItems, filteredReports]);
    // State management: Set loading state
    const setLoading = useCallback((loading) => {
        setState(prevState => ({
            ...prevState,
            loading,
        }));
    }, []);
    // State management: Set error state
    const setError = useCallback((error) => {
        setState(prevState => ({
            ...prevState,
            error,
        }));
    }, []);
    // State management: Reset to initial state
    const reset = useCallback(() => {
        setState(createInitialState(initialPath));
    }, [initialPath]);
    // Pagination: Handle page changes in controlled mode
    // Updates internal page state and calls parent's onPageChange callback
    const handlePageChange = useCallback((newPage) => {
        // Clamp the page to valid range before setting
        const clampedPage = totalPagesCount !== undefined
            ? clampPage(newPage, totalPagesCount)
            : newPage;
        setInternalPage(clampedPage);
        if (onPageChange) {
            onPageChange(clampedPage);
        }
    }, [onPageChange, totalPagesCount]);
    // Pagination: Export total pages (for use in consumers)
    const totalPages = totalPagesCount;
    // Computed values
    const canGoBack = state.navigation.historyIndex > 0;
    const canGoForward = state.navigation.historyIndex < state.navigation.pathHistory.length - 1;
    const hasSelection = state.selection.selectionCount > 0;
    // Context value
    const contextValue = {
        // State
        state,
        // Navigation actions
        navigateTo,
        navigateBack,
        navigateForward,
        navigateUp,
        navigateToRoot,
        canGoBack,
        canGoForward,
        // Selection actions
        selectItem,
        deselectItem,
        toggleItemSelection,
        selectItems,
        selectAllItems,
        clearSelection,
        isItemSelected,
        // Bulk operations
        getSelectedFiles,
        getSelectedFolders,
        hasSelection,
        // Selection reconciliation (for filter integration)
        getVisibleSelections,
        getHiddenSelections,
        // State management
        setLoading,
        setError,
        reset,
        // Controlled mode props (for filter integration)
        filteredReports,
        isFilterLoading: isLoading,
        filterError: error,
        paginationMetadata,
        onPageChange: handlePageChange,
        onPageSizeChange,
    };
    return (React.createElement(S3BrowserContext.Provider, { value: contextValue }, children));
};
export default S3BrowserContext;
